import { useEffect, useState } from 'react';

import {
  DEFAULT_GIFTCARD_REASONS,
  DEFAULT_GIFTCARD_SOURCES,
  DEFAULT_GIFTCARD_STATES,
} from './constants';
import { fetchGiftcardConstants } from './fetchGiftcards';
import type { ConstantOption, GiftcardConstants } from './types';

const FALLBACK: GiftcardConstants = {
  states: DEFAULT_GIFTCARD_STATES,
  sources: DEFAULT_GIFTCARD_SOURCES,
  reasons: DEFAULT_GIFTCARD_REASONS,
};

/**
 * Enforce current Trove terminology on API-provided source labels: the
 * "Issued MiTi" / "Issued INSTi" sources become "Issued Digital Trade-in
 * (DITI)" / "Issued In-store (INSTI)".
 */
const normalizeSources = (options: ConstantOption[]): ConstantOption[] =>
  options.map(({ code, displayName }) => ({
    code,
    displayName: displayName
      .replace(/\bMiTi\b/g, 'Digital Trade-in (DITI)')
      .replace(/\bINSTi\b/g, 'In-store (INSTI)'),
  }));

/**
 * All three lists must be non-empty. Checking only `states` left the same hole this exists to
 * close: `{states: [...], sources: [], reasons: []}` was cached as success, so the Source and
 * Reason dropdowns rendered empty with no fallback banner, every Source cell showed a raw code
 * like ISSUED_MITI instead of its label, and the cache blocked any retry for the session.
 */
const usableConstants = (result: GiftcardConstants | null | undefined): boolean =>
  Boolean(
    result &&
      [result.states, result.sources, result.reasons].every(
        list => Array.isArray(list) && list.length > 0
      )
  );

let cache: GiftcardConstants | null = null;

/**
 * Test seam. The cache has no other invalidation point for the lifetime of the process, so
 * without this each test inherits whatever the previous one left behind — which made the suite
 * order-dependent and, worse, made one real regression fail several unrelated tests.
 */
export const resetGiftcardConstantsCache = (): void => {
  cache = null;
};

/**
 * Gift card states, sources, and reasons for the filter dropdowns. Serves
 * sensible defaults immediately, refined from the API once available.
 * `isFallback` is true when the live fetch failed and we're stuck on defaults —
 * callers surface that so stale options aren't mistaken for the real set.
 */
export const useGiftcardConstants = (): GiftcardConstants & { isFallback: boolean } => {
  const [constants, setConstants] = useState<GiftcardConstants>(cache ?? FALLBACK);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    if (cache) return;
    let cancelled = false;
    fetchGiftcardConstants()
      .then((result) => {
        // A response that resolves is not necessarily a usable one. An empty or partial payload
        // would otherwise be cached as success: no banner, empty dropdowns that look unloaded,
        // raw state codes in every chip, and — because the cache blocks refetching — no recovery
        // short of a hard reload. A partial one is worse, since `toChoices(undefined)` throws
        // during render.
        if (!usableConstants(result)) {
          console.error('Gift card constants came back unusable; keeping defaults:', result);
          if (!cancelled) setIsFallback(true);
          return;
        }
        cache = { ...result, sources: normalizeSources(result.sources) };
        if (!cancelled) setConstants(cache);
      })
      .catch((error) => {
        console.error('Falling back to default gift card filter options:', error);
        if (!cancelled) setIsFallback(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...constants, isFallback };
};
